"use client";
import React, { useState } from "react";
import { Button } from "@mui/base/Button";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
} from "@mui/material";
import TextInput, { Label } from "../common/form/TextInput";
import { reviewInitalValues } from "../common/form/initalValues";
import { reviewSchema } from "../common/form/validationSchema";
import { useFormik } from "formik";
import { BiChevronRight, BiCommentAdd, BiXCircle } from "react-icons/bi";
import ReactStars from "react-stars";
import { ProductReview } from "@/lib/http";
import { IProductDetails } from "@/lib/interface";
import { useRouter } from "next/navigation";
import { revalidatePath } from "next/cache";
import ReCAPTCHA from "react-google-recaptcha";

const AddProductReview: React.FC<{ product: IProductDetails }> = ({
  product,
}) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  const formInuput = [
    {
      type: "text",
      name: "Name",
      label: "Name",
      isRequired: true,
      size: "half",
    },
    {
      type: "email",
      name: "EmailID",
      label: "Email",
      isRequired: true,
      size: "half",
    },

    {
      type: "text",
      multiline: true,
      rows: 3,
      name: "Message",
      label: "Review",
      size: "full",
    },
  ];

  const onSubmit = async (values: Record<string, any>) => {
    setLoading(true);
    if (verified) {
      try {
        const res = await ProductReview.api("post", {
          data: {
            ...values,
            ProductID: product.ProductDetails.ProductID,
          },
        });

        if (res?.status === 200)
          if (res.data.status === "success") {
            setOpen(false);
            router.refresh();
          }
      } catch (error) {
        console.log(error);
      }
      resetForm();
    }
    setLoading(false);
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    handleChange,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: reviewInitalValues,
    validationSchema: reviewSchema,
    onSubmit,
  });
  return (
    <>
      {product.Reviews.length ? (
        <Button
          onClick={handleOpen}
          className=" group mt-5 overflow-y-hidden  bg-primary-main  py-2 px-6 hover:border hover:border-primary-main font-semibold rounded-full flex items-center relative  before:absolute before:bottom-0 before:left-0 before:bg-white before:translate-y-48 hover:before:translate-y-0 before:transition-all before:w-full before:h-full before:rounded-full mb-5"
        >
          <p className="relative flex items-center group-hover:!text-primary-main !text-white ">
            <BiCommentAdd className="mr-1" /> Add Review
          </p>
        </Button>
      ) : (
        <Button
          onClick={handleOpen}
          disabled={loading}
          className=" group mt-5 overflow-y-hidden  bg-primary-main  py-2 px-6 hover:border hover:border-primary-main font-semibold rounded-full flex items-center relative  before:absolute before:bottom-0 before:left-0 before:bg-white before:translate-y-48  hover:before:translate-y-0 before:transition-all before:w-full before:h-full before:rounded-full"
          type="submit"
        >
          <p className="relative flex items-center text-[16px] group-hover:!text-primary-main !text-white">
            <BiCommentAdd className="mr-1 !text-2xl" /> Add Review
          </p>
        </Button>
      )}

      <Dialog maxWidth="sm" fullWidth={true} open={open} onClose={handleClose}>
        <DialogContent>
          <div className=" bg-white ">
            <div className=" flex justify-between items-center">
              <h3 className="text-xl text-left mb-1 text-secondary-dark font-bold ">
                Please Add Your Review
              </h3>
              <BiXCircle
                fontSize={30}
                className="text-orange-main hover:text-primary-main transition-all duration-100 hover:duration-200 cursor-pointer"
                onClick={handleClose}
              />
            </div>
            <Divider
              sx={{
                marginY: 2,
              }}
            />

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-2 contact-form">
                <div className="grid-cols-2">
                  <Label>Ratings</Label>
                  <ReactStars
                    value={+values.RatingValue}
                    onChange={(e) => setFieldValue("RatingValue", `${e}`)}
                    size={20}
                    half={false}
                    color2={"#eab308"}
                  />
                </div>
                <div className="md:grid md:grid-cols-2 gap-2 ">
                  {formInuput?.map((f, i) => {
                    const { size, name, ...rest } = f;
                    return (
                      <div
                        key={name}
                        className={size === "full" ? "col-span-2" : "my-2 md:my-0"}
                      >
                        <TextInput
                          size="small"
                          {...rest}
                          name={name}
                          value={values[name] || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched[name] && Boolean(errors[name])}
                          helperText={(touched[name] && errors[name]) || ""}
                        />
                      </div>
                    );
                  })}
                </div>
                <ReCAPTCHA
                    sitekey="6LfR2rkpAAAAAF4N_ajW4BA0Wr7bVdinnKS3tdW5"
                    onChange={() => {
                      setVerified(true);
                    }}
                    
                    type="image"
                    style={{
                      width:'100%',
                    }}
                  />
              </div>
            
              <Button
                disabled={loading}
                className=" group mt-5 overflow-y-hidden  bg-primary-main  py-2 px-6 hover:border hover:border-primary-main font-semibold rounded-full flex items-center relative  before:absolute before:bottom-0 before:left-0 before:bg-white before:translate-y-12 hover:before:translate-y-0 before:transition-all before:w-full before:h-full before:rounded-full"
                type="submit"
              >
                <p className="relative flex items-center group-hover:text-primary-main text-white">
                  Submit{" "}
                  {loading ? (
                    <CircularProgress size={10} className="text-white mx-2" />
                  ) : (
                    <BiChevronRight className="text-xl" />
                  )}
                </p>
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddProductReview;
